import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Specs table component.
 *
 * Swaps the table body when the selected variant changes, so rows reading a variant metafield show
 * the right value. The whole tbody is replaced rather than each row patched, because a row can
 * appear or disappear between variants (a row renders nothing when its metafield has no value).
 *
 * Listens on `document`, not on the closest section: the variant picker lives in the `main` section
 * while this block sits in the product details section, so the bubbling event never reaches a
 * section-scoped listener. Because the listener is document-wide and a page can hold more than one
 * specs table, the incoming table is matched on this block's id.
 */
class ApcSpecsTable extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    // Created per connect: an aborted controller's signal would silently drop the listener if
    // this element is ever moved and reconnected.
    this.#abortController = new AbortController();
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const blockId = this.dataset.blockId;
    const html = event.detail.data?.html;
    if (!blockId || !html) return;

    // Absent when the update came from a product card or quick-add, whose rendering section does
    // not include this block. Leaving the current rows in place is correct there.
    const incoming = html.querySelector(`apc-specs-table[data-block-id="${CSS.escape(blockId)}"] tbody`);
    const current = this.querySelector('tbody');
    if (!incoming || !current) return;

    // Morph rather than assign innerHTML: it keeps untouched rows (and any open info dialog inside
    // them) alive instead of tearing the whole body down and rebuilding it.
    morph(current, incoming);
  };
}

if (!customElements.get('apc-specs-table')) {
  customElements.define('apc-specs-table', ApcSpecsTable);
}
